import { useEffect, useState } from "react";
import LegalFooter from "../components/LegalFooter";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatErr } from "../lib/api";
import { Loader2, Film, Radio, Camera, Mail, ShieldCheck, ArrowLeft, Gift, Eye, EyeOff } from "lucide-react";
import CloudinaryUpload from "../components/CloudinaryUpload";

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳" }, { code: "+1", flag: "🇺🇸" }, { code: "+44", flag: "🇬🇧" },
  { code: "+971", flag: "🇦🇪" }, { code: "+61", flag: "🇦🇺" }, { code: "+65", flag: "🇸🇬" },
  { code: "+880", flag: "🇧🇩" }, { code: "+92", flag: "🇵🇰" }, { code: "+977", flag: "🇳🇵" },
];

const LANGUAGES = [
  { code: "en", name: "English", native: "English" },
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "bn", name: "Bengali", native: "বাংলা" },
  { code: "mr", name: "Marathi", native: "मराठी" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "or", name: "Odia", native: "ଓଡ଼ିଆ" },
];

// Step 1 = enter email, Step 2 = OTP, Step 3 = full signup form
export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRef = (params.get("ref") || "").trim().toLowerCase();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [resentAt, setResentAt] = useState(0);
  const [refCode, setRefCode] = useState(initialRef);
  const [form, setForm] = useState({ name: "", username: "", password: "", phone: "", country_code: "+91", language: "en", primary_content: "reels", avatar: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  useEffect(() => { if (initialRef) setRefCode(initialRef); }, [initialRef]);

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setLoading(true); setOtpInfo("");
    try {
      const { data } = await api.post("/auth/send-email-otp", { email: email.trim().toLowerCase() });
      setStep(2);
      setResentAt(Date.now());
      setOtpInfo(data.sent
        ? `We've sent a 6-digit code to ${email}. Check your inbox (and spam folder).`
        : `Code generated. (Email delivery is offline — ask Admin or check server logs to retrieve it.)`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/verify-email-otp", { email: email.trim().toLowerCase(), otp: otp.trim() });
      setStep(3);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const [agree, setAgree] = useState(false);
  const submitSignup = async (e) => {
    e.preventDefault();
    if (form.password !== confirmPw) { setErr("Passwords do not match"); return; }
    setErr(""); setLoading(true);
    try {
      await register({ ...form, email: email.trim().toLowerCase(), email_otp: otp.trim(), ref_code: (refCode || "").trim().toLowerCase() || undefined });
      nav("/");
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  return (
    <div className="min-h-screen bg-mesh flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-5">
          <img src="/logo512.png" alt="Hifireels" className="w-16 h-16 mx-auto rounded-2xl object-cover mb-2 shadow-[0_0_28px_rgba(255,42,95,0.4)]"/>
          <h1 className="font-outfit font-extrabold text-3xl">Join Hifireels</h1>
          <p className="text-white/60 mt-0.5 text-sm">Create your account in a few steps</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-3" data-testid="signup-stepper">
          {[1,2,3].map(n => (
            <div key={n} className={`h-1.5 w-12 rounded-full ${step >= n ? "bg-[#00E5FF]" : "bg-white/15"}`}/>
          ))}
        </div>

        {/* STEP 1: Email */}
        {step === 1 && (
          <form onSubmit={sendOtp} className="glass rounded-3xl p-5 space-y-3" data-testid="signup-step-1">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#00E5FF]"/>
              <h2 className="font-outfit font-bold text-lg">Verify your email</h2>
            </div>
            <p className="text-xs text-white/60">We'll send a 6-digit code to confirm your email before creating the account.</p>
            <input data-testid="signup-email" type="email" required placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
            {err && <div data-testid="signup-error" className="text-[#FF2A5F] text-sm">{err}</div>}
            <button data-testid="send-otp-btn" disabled={loading || !email}
              className="w-full py-3 rounded-full bg-[#00E5FF] text-black font-outfit font-bold neon-cyan active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Send verification code</>}
            </button>
            <Link to="/login" className="block text-center text-sm text-white/70 hover:text-[#00E5FF] pt-1">Already have an account? Sign in</Link>
            <Link to="/forgot" data-testid="signup-forgot-link" className="block text-center text-xs text-white/55 hover:text-[#FF7A00]">Forgot password?</Link>
          </form>
        )}

        {/* STEP 2: OTP */}
        {step === 2 && (
          <form onSubmit={verifyOtp} className="glass rounded-3xl p-5 space-y-3" data-testid="signup-step-2">
            <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-xs text-white/70" data-testid="otp-back"><ArrowLeft className="w-3.5 h-3.5"/>Change email</button>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#FF7A00]"/>
              <h2 className="font-outfit font-bold text-lg">Enter verification code</h2>
            </div>
            {otpInfo && <p className="text-xs text-white/70 bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded-xl p-2.5" data-testid="otp-info">{otpInfo}</p>}
            <input data-testid="otp-input" required inputMode="numeric" maxLength={6} placeholder="6-digit code"
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0,6))}
              className="w-full px-4 py-3 text-center tracking-[10px] text-2xl font-bold rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
            {err && <div data-testid="signup-error" className="text-[#FF2A5F] text-sm">{err}</div>}
            <button data-testid="verify-otp-btn" disabled={loading || otp.length !== 6}
              className="w-full py-3 rounded-full bg-[#FF7A00] text-black font-outfit font-bold active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Verify code"}
            </button>
            <button type="button" data-testid="resend-otp-btn"
              onClick={(e) => sendOtp(e)} disabled={loading || (Date.now() - resentAt) < 30000}
              className="w-full text-xs text-white/70 hover:text-[#00E5FF] py-1 disabled:opacity-40">
              {Date.now() - resentAt < 30000 ? `Resend code in ${Math.ceil((30000 - (Date.now() - resentAt))/1000)}s` : "Didn't get the code? Resend"}
            </button>
          </form>
        )}

        {/* STEP 3: Account details */}
        {step === 3 && (
          <form onSubmit={submitSignup} className="glass rounded-3xl p-5 space-y-2.5" data-testid="signup-step-3">
            <div className="flex items-center justify-between">
              <h2 className="font-outfit font-bold text-lg">Complete your profile</h2>
              <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5"/>{email}</span>
            </div>

            {/* Profile photo */}
            <div className="flex flex-col items-center gap-2 mb-1">
              <div className="relative">
                {form.avatar ? (
                  <img src={form.avatar} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#00E5FF]" data-testid="signup-avatar-preview"/>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center">
                    <Camera className="w-7 h-7 text-white/40"/>
                  </div>
                )}
              </div>
              <div className="w-full max-w-[260px]">
                <CloudinaryUpload kind="image" maxMB={5} testId="signup-photo"
                  currentUrl={form.avatar || null}
                  onUploaded={(d) => setForm(f => ({...f, avatar: d.secure_url}))} />
              </div>
              <p className="text-[10px] text-white/50">Profile photo (optional)</p>
            </div>

            <input data-testid="signup-name" required placeholder="Full name" value={form.name} onChange={upd("name")} className="w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
            <input data-testid="signup-username" placeholder="Username (optional)" autoCapitalize="none" autoCorrect="off" spellCheck="false" value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value.toLowerCase().replace(/[\s@]/g, "")}))} className="w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
            <div className="flex gap-2">
              <select data-testid="signup-country" value={form.country_code} onChange={upd("country_code")} className="px-2 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none w-24 text-sm">
                {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-[#0B0F19]">{c.flag} {c.code}</option>)}
              </select>
              <input data-testid="signup-phone" type="tel" required pattern="[0-9]{6,15}" inputMode="numeric"
                placeholder="Mobile number" value={form.phone}
                onChange={(e)=>setForm(f=>({...f, phone: e.target.value.replace(/\D/g,"")}))}
                className="flex-1 px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
            </div>
            <div className="relative">
              <input data-testid="signup-password" type={showPw ? "text" : "password"} required minLength={6} placeholder="Password (min 6)" value={form.password} onChange={upd("password")} className="w-full px-4 py-2.5 pr-12 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
              <button type="button" onClick={() => setShowPw(v => !v)} data-testid="signup-password-eye" aria-label="Show password" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                {showPw ? <EyeOff className="w-5 h-5"/> : <Eye className="w-5 h-5"/>}
              </button>
            </div>
            <div className="relative">
              <input data-testid="signup-confirm-password" type={showPw2 ? "text" : "password"} required minLength={6} placeholder="Confirm password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                className={`w-full px-4 py-2.5 pr-12 rounded-2xl bg-white/5 border outline-none focus:border-[#00E5FF] ${confirmPw && confirmPw !== form.password ? "border-[#FF2A5F]" : "border-white/10"}`}/>
              <button type="button" onClick={() => setShowPw2(v => !v)} data-testid="signup-confirm-password-eye" aria-label="Show confirm password" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                {showPw2 ? <EyeOff className="w-5 h-5"/> : <Eye className="w-5 h-5"/>}
              </button>
            </div>
            {confirmPw && confirmPw !== form.password && <p className="text-[11px] text-[#FF2A5F] -mt-1" data-testid="signup-password-mismatch">Passwords do not match</p>}

            <div>
              <label className="text-[10px] uppercase text-white/60 font-semibold">Primary Language</label>
              <select data-testid="signup-language" value={form.language} onChange={upd("language")} className="mt-1 w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none">
                {LANGUAGES.map(l => <option key={l.code} value={l.code} className="bg-[#0B0F19]">{l.native} • {l.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase text-white/60 font-semibold mb-1 block">Primary Content</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={()=>setForm(f=>({...f, primary_content:"reels"}))} data-testid="signup-content-reels"
                  className={`p-2.5 rounded-2xl border text-xs font-bold flex items-center justify-center gap-1 ${form.primary_content==="reels"?"border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]":"border-white/10 bg-white/5"}`}>
                  <Film className="w-4 h-4"/>Short Video / Reels
                </button>
                <button type="button" onClick={()=>setForm(f=>({...f, primary_content:"live"}))} data-testid="signup-content-live"
                  className={`p-2.5 rounded-2xl border text-xs font-bold flex items-center justify-center gap-1 ${form.primary_content==="live"?"border-[#FF2A5F] bg-[#FF2A5F]/10 text-[#FF2A5F]":"border-white/10 bg-white/5"}`}>
                  <Radio className="w-4 h-4"/>Live Streaming
                </button>
              </div>
            </div>

            <p className="text-[10px] text-white/40">By signing up, you agree to share your phone & email for account verification & marketing communications.</p>

            {/* Referral code (optional) */}
            <div className="rounded-2xl bg-gradient-to-r from-[#FBBF24]/10 to-[#FF7A00]/10 border border-[#FBBF24]/25 p-2.5">
              <label className="text-[10px] uppercase text-[#FBBF24] font-bold flex items-center gap-1"><Gift className="w-3 h-3"/>Referral code (optional)</label>
              <input data-testid="signup-refcode" placeholder="Enter friend's username to get a FREE spin"
                value={refCode} onChange={(e) => setRefCode(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10 outline-none text-sm focus:border-[#FBBF24]"/>
              <p className="text-[10px] text-white/50 mt-1">You + your friend each get 1 FREE spin on the wheel 🎁</p>
            </div>

            <label className="flex items-start gap-2 text-xs text-white/80 py-1" data-testid="signup-agree-label">
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} data-testid="signup-agree" className="mt-0.5 w-4 h-4 accent-[#00E5FF]"/>
              <span>I agree to the <Link to="/policy?tab=terms" target="_blank" className="text-[#00E5FF] underline">Terms & Conditions</Link>, <Link to="/policy?tab=privacy" target="_blank" className="text-[#00E5FF] underline">Privacy Policy</Link> and <Link to="/policy?tab=community" target="_blank" className="text-[#00E5FF] underline">Community Guidelines</Link></span>
            </label>
            {err && <div data-testid="signup-error" className="text-[#FF2A5F] text-sm">{err}</div>}
            <button data-testid="signup-submit" disabled={loading || !agree} className="w-full py-3 rounded-full bg-[#00E5FF] text-black font-outfit font-bold neon-cyan active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create Account"}
            </button>
            <Link to="/login" className="block text-center text-sm text-white/70 hover:text-[#00E5FF] pt-1">Already have an account? Sign in</Link>
            <Link to="/forgot" data-testid="signup-forgot-link" className="block text-center text-xs text-white/55 hover:text-[#FF7A00]">Forgot password?</Link>
          </form>
        )}
        <LegalFooter/>
      </div>
    </div>
  );
}
